/**
 * Goal 2: agreement next-job create report/order as one dialog-owned operation.
 * Covers list table, stack list, and detail next-job section.
 * Dialog owns feedback — no axiosWithToast for the create call.
 */
import {
  getOperationCoordinator,
  STEP_STATUS,
  OUTCOME,
  normalizeError,
  hasMeaningfulErrors,
  canConfirmStepFromEvidence,
  outcomeAfterRefreshFailure,
} from "../../../../operationFeedback";
import {
  createReportServiceAgreement,
  createOrderServiceAgreement,
} from "../../../../api/service/agreement";

export const CREATE_NEXT_JOB_OPERATION_KEY = "create_next_job";

/**
 * @param {'report'|'order'} jobKind
 * @param {object} agreement
 */
export function evaluateCreateNextJobPreflight(jobKind, agreement) {
  if (!agreement?.id) {
    return { ready: false, reason: "missing_id" };
  }
  if (jobKind !== "report" && jobKind !== "order") {
    return { ready: false, reason: "invalid_kind" };
  }
  return { ready: true, reason: null };
}

function defaultExecutionSteps(t, jobKind) {
  return [
    {
      key: "preflight",
      label: t("operation_feedback.create_next_job.step.preflight"),
      status: STEP_STATUS.PENDING,
    },
    {
      key: "create",
      label:
        jobKind === "order"
          ? t("operation_feedback.create_next_job.step.create_order")
          : t("operation_feedback.create_next_job.step.create_report"),
      status: STEP_STATUS.PENDING,
    },
    {
      key: "refresh",
      label: t("operation_feedback.create_next_job.step.refresh"),
      status: STEP_STATUS.PENDING,
    },
  ];
}

function extractResource(payload, jobKind) {
  const data = payload?.data ?? payload;
  if (jobKind === "order") {
    return data?.order || data;
  }
  return data?.report || data;
}

function confirmedCreatedResource(resource) {
  const id = resource?.id;
  if (id == null || id === "") return null;
  return resource;
}

function resourceLink(jobKind, resource, t) {
  if (!resource?.id) return [];
  if (jobKind === "order") {
    return [
      {
        href: `/dashboard/orders/${resource.id}`,
        label:
          resource.displayTitle ||
          resource.order_number ||
          t("operation_feedback.create_next_job.open_order", {
            id: resource.id,
          }),
      },
    ];
  }
  return [
    {
      href: `/dashboard/service/reports/${resource.id}`,
      label:
        resource.displayTitle ||
        resource.report_number ||
        t("operation_feedback.create_next_job.open_report", {
          id: resource.id,
        }),
    },
  ];
}

/**
 * @param {object} args
 * @param {'report'|'order'} args.jobKind
 * @param {object} args.agreement
 * @param {string|number} [args.companyId]
 * @param {Function} [args.onRefresh]
 * @param {Function} [args.onCreated] - called with created resource on success
 * @param {Function} args.t
 * @param {Function} [args.createReportFn]
 * @param {Function} [args.createOrderFn]
 */
export async function runCreateNextJobOperation({
  jobKind,
  agreement,
  companyId,
  onRefresh,
  onCreated,
  t,
  createReportFn = createReportServiceAgreement,
  createOrderFn = createOrderServiceAgreement,
}) {
  const coordinator = getOperationCoordinator();
  const resourceId = agreement?.id;
  const title =
    jobKind === "order"
      ? t("operation_feedback.create_next_job.title_order", {
          id: resourceId || "",
        })
      : t("operation_feedback.create_next_job.title_report", {
          id: resourceId || "",
        });

  const op = coordinator.start({
    operationKey: CREATE_NEXT_JOB_OPERATION_KEY,
    title,
    presentationOwner: "dialog",
    resourceId,
    companyId,
    steps: defaultExecutionSteps(t, jobKind),
  });
  const id = op.clientOperationId;
  let rev = 1;

  const preflight = evaluateCreateNextJobPreflight(jobKind, agreement);

  if (
    !canConfirmStepFromEvidence({
      fromReadiness: true,
      status: STEP_STATUS.COMPLETED,
    })
  ) {
    /* intentional gate */
  }

  if (!preflight.ready) {
    const detail = t("operation_feedback.create_next_job.preflight_failed");
    coordinator.updateStep(id, "preflight", {
      status: STEP_STATUS.NEEDS_FIX,
      detail,
      revision: rev++,
    });
    coordinator.updateStep(id, "create", {
      status: STEP_STATUS.NOT_DONE,
      revision: rev++,
    });
    coordinator.updateStep(id, "refresh", {
      status: STEP_STATUS.NOT_RELEVANT,
      revision: rev++,
    });
    coordinator.complete(id, {
      summary: detail,
      outcome: OUTCOME.NOT_APPLIED,
    });
    return { clientOperationId: id, ok: false, error: { kind: "preflight" } };
  }

  coordinator.updateStep(id, "preflight", {
    status: STEP_STATUS.COMPLETED,
    detail: t("operation_feedback.create_next_job.preflight_ok"),
    revision: rev++,
  });

  coordinator.updateStep(id, "create", {
    status: STEP_STATUS.RUNNING,
    revision: rev++,
  });

  let response;
  try {
    const createFn = jobKind === "order" ? createOrderFn : createReportFn;
    response = await createFn({ id: agreement.id });
  } catch (err) {
    const normalized = normalizeError(err, {
      operationKey: CREATE_NEXT_JOB_OPERATION_KEY,
    });
    coordinator.updateStep(id, "create", {
      status: STEP_STATUS.FAILED,
      detail: normalized.safeMessage,
      revision: rev++,
    });
    coordinator.updateStep(id, "refresh", {
      status: STEP_STATUS.NOT_DONE,
      revision: rev++,
    });
    coordinator.complete(id, {
      summary: normalized.safeMessage,
      outcome: OUTCOME.NOT_APPLIED,
      requestId: normalized.requestId || null,
      error: {
        safeMessage: normalized.safeMessage,
        requestId: normalized.requestId || null,
        code: normalized.code,
        kind: normalized.kind,
      },
    });
    return { clientOperationId: id, ok: false, error: normalized };
  }

  const payload = response?.data ?? response;
  const httpStatus = response?.status;
  if (
    (httpStatus && httpStatus >= 400) ||
    hasMeaningfulErrors(payload?.errors) ||
    payload?.error === true
  ) {
    const normalized = normalizeError(
      { response: { status: httpStatus || 422, data: payload } },
      { operationKey: CREATE_NEXT_JOB_OPERATION_KEY }
    );
    coordinator.updateStep(id, "create", {
      status: STEP_STATUS.FAILED,
      detail: normalized.safeMessage,
      revision: rev++,
    });
    coordinator.updateStep(id, "refresh", {
      status: STEP_STATUS.NOT_DONE,
      revision: rev++,
    });
    coordinator.complete(id, {
      summary: normalized.safeMessage,
      outcome: OUTCOME.NOT_APPLIED,
      requestId: normalized.requestId || null,
      error: {
        safeMessage: normalized.safeMessage,
        requestId: normalized.requestId || null,
        code: normalized.code,
        kind: normalized.kind,
      },
    });
    return { clientOperationId: id, ok: false, error: normalized, payload };
  }

  const created = confirmedCreatedResource(extractResource(payload, jobKind));
  if (!created) {
    const detail = t("operation_feedback.create_next_job.create_unconfirmed");
    coordinator.updateStep(id, "create", {
      status: STEP_STATUS.UNCONFIRMED,
      detail,
      revision: rev++,
    });
    coordinator.updateStep(id, "refresh", {
      status: STEP_STATUS.NOT_DONE,
      revision: rev++,
    });
    coordinator.complete(id, {
      summary: detail,
      outcome: OUTCOME.UNKNOWN,
    });
    return { clientOperationId: id, ok: false, error: { kind: "unconfirmed" }, payload };
  }

  const links = resourceLink(jobKind, created, t);

  coordinator.updateStep(id, "create", {
    status: STEP_STATUS.COMPLETED,
    detail:
      jobKind === "order"
        ? t("operation_feedback.create_next_job.create_order_ok")
        : t("operation_feedback.create_next_job.create_report_ok"),
    resultRef: created?.id ? { id: created.id } : undefined,
    revision: rev++,
  });

  if (typeof onCreated === "function" && created) {
    try {
      onCreated(created);
    } catch (navErr) {
      console.error(navErr);
    }
  }

  if (typeof onRefresh === "function") {
    coordinator.updateStep(id, "refresh", {
      status: STEP_STATUS.RUNNING,
      revision: rev++,
    });
    try {
      await Promise.resolve(onRefresh(agreement));
      coordinator.updateStep(id, "refresh", {
        status: STEP_STATUS.COMPLETED,
        revision: rev++,
      });
      const afterOk = outcomeAfterRefreshFailure(STEP_STATUS.COMPLETED, false);
      coordinator.complete(id, {
        summary:
          jobKind === "order"
            ? t("operation_feedback.create_next_job.success_order")
            : t("operation_feedback.create_next_job.success_report"),
        outcome: afterOk.outcome || OUTCOME.APPLIED,
        links,
      });
      return { clientOperationId: id, ok: true, payload: created };
    } catch (refreshErr) {
      const normalized = normalizeError(refreshErr, {
        operationKey: CREATE_NEXT_JOB_OPERATION_KEY,
      });
      const afterFail = outcomeAfterRefreshFailure(STEP_STATUS.COMPLETED, true);
      coordinator.updateStep(id, "refresh", {
        status: afterFail.refreshStatus || STEP_STATUS.FAILED,
        detail: normalized.safeMessage,
        revision: rev++,
      });
      coordinator.complete(id, {
        summary: t(
          afterFail.summaryKey || "operation_feedback.saved_list_refresh_failed"
        ),
        outcome: afterFail.outcome || OUTCOME.PARTIAL,
        links,
      });
      return {
        clientOperationId: id,
        ok: true,
        payload: created,
        refreshFailed: true,
      };
    }
  }

  coordinator.updateStep(id, "refresh", {
    status: STEP_STATUS.NOT_RELEVANT,
    revision: rev++,
  });
  coordinator.complete(id, {
    summary:
      jobKind === "order"
        ? t("operation_feedback.create_next_job.success_order")
        : t("operation_feedback.create_next_job.success_report"),
    outcome: OUTCOME.APPLIED,
    links,
  });
  return { clientOperationId: id, ok: true, payload: created };
}
